/**
 * Cross-platform Audio Recorder
 * Resilient recording for iOS Safari, Android Chrome, and Desktop browsers
 * 
 * Key features:
 * - iOS WAV fallback (avoids fragmented MP4 issues)
 * - AudioContext resume handling for iOS
 * - Audio level monitoring (detects silent/failed recordings)
 * - Automatic retry on permission issues
 * - Warmup period to ensure mic is ready
 */

type StartOptions = {
  mimeType?: string;
  timesliceMs?: number;
  onAudioLevel?: (level: number) => void; // 0-1 normalized audio level
};

type RecorderState = 'idle' | 'starting' | 'recording' | 'stopping';

class AudioRecorder {
  private mediaRecorder?: MediaRecorder;
  private chunks: BlobPart[] = [];
  private stream?: MediaStream;
  private mimeType: string = 'audio/webm;codecs=opus';
  private onChunkCallback?: (blob: Blob) => void;
  private state: RecorderState = 'idle';
  
  // iOS Safari fallback: record raw PCM via WebAudio and encode WAV
  private wavCtx?: AudioContext;
  private wavSource?: MediaStreamAudioSourceNode;
  private wavProcessor?: ScriptProcessorNode;
  private wavGain?: GainNode;
  private wavBuffers: Float32Array[] = [];
  private wavSampleRate: number = 44100;
  private usingWav = false;
  
  // Audio level monitoring
  private analyser?: AnalyserNode;
  private levelCheckInterval?: ReturnType<typeof setInterval>;
  private onAudioLevelCallback?: (level: number) => void;
  private hasDetectedAudio = false;
  private peakLevel = 0;

  // Platform detection
  private platformInfo = this.detectPlatform();

  private detectPlatform() {
    const ua = navigator.userAgent;
    const isIOS = /iPad|iPhone|iPod/.test(ua);
    const isAndroid = /Android/.test(ua);
    const isSafari = /Safari/.test(ua) && !/Chrome/.test(ua);
    const isChrome = /Chrome/.test(ua) && !/Edge/.test(ua);
    const isFirefox = /Firefox/.test(ua);
    
    return {
      isIOS,
      isAndroid,
      isSafari,
      isChrome,
      isFirefox,
      isMobile: isIOS || isAndroid,
      isDesktop: !isIOS && !isAndroid,
      // iOS Chrome/Firefox still use WebKit
      isIOSWebKit: isIOS,
      description: isIOS ? 'iOS' : isAndroid ? 'Android' : 'Desktop'
    };
  }

  private encodeWav(buffers: Float32Array[], sampleRate: number): Blob {
    const numChannels = 1;
    const bitsPerSample = 16;
    const bytesPerSample = bitsPerSample / 8;
    const blockAlign = numChannels * bytesPerSample;
    const byteRate = sampleRate * blockAlign;
    const dataLength = buffers.reduce((sum, b) => sum + b.length, 0) * bytesPerSample;
    const buffer = new ArrayBuffer(44 + dataLength);
    const view = new DataView(buffer);

    const writeString = (offset: number, s: string) => {
      for (let i = 0; i < s.length; i++) view.setUint8(offset + i, s.charCodeAt(i));
    };

    let offset = 0;
    writeString(offset, 'RIFF'); offset += 4;
    view.setUint32(offset, 36 + dataLength, true); offset += 4;
    writeString(offset, 'WAVE'); offset += 4;
    writeString(offset, 'fmt '); offset += 4;
    view.setUint32(offset, 16, true); offset += 4;
    view.setUint16(offset, 1, true); offset += 2;
    view.setUint16(offset, numChannels, true); offset += 2;
    view.setUint32(offset, sampleRate, true); offset += 4;
    view.setUint32(offset, byteRate, true); offset += 4;
    view.setUint16(offset, blockAlign, true); offset += 2;
    view.setUint16(offset, bitsPerSample, true); offset += 2;
    writeString(offset, 'data'); offset += 4;
    view.setUint32(offset, dataLength, true); offset += 4;

    let writePos = 44;
    for (const chunk of buffers) {
      for (let i = 0; i < chunk.length; i++) {
        const s = Math.max(-1, Math.min(1, chunk[i]));
        view.setInt16(writePos, s < 0 ? s * 0x8000 : s * 0x7fff, true);
        writePos += 2;
      }
    }

    return new Blob([buffer], { type: 'audio/wav' });
  }

  /**
   * Check and request microphone permission
   * Returns: 'granted' | 'denied' | 'prompt' | 'error'
   */
  async checkPermission(): Promise<string> {
    try {
      // Try the Permissions API first (not supported on all browsers)
      if (navigator.permissions && navigator.permissions.query) {
        try {
          const result = await navigator.permissions.query({ name: 'microphone' as PermissionName });
          return result.state;
        } catch {
          // Permissions API not supported for microphone, continue
        }
      }
      
      // Fallback: try to get stream briefly to check permission
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      stream.getTracks().forEach(t => t.stop());
      return 'granted';
    } catch (err: any) {
      if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
        return 'denied';
      }
      return 'error';
    }
  }

  /**
   * Get audio constraints optimized for each platform
   */
  private getAudioConstraints(): MediaTrackConstraints {
    const base: MediaTrackConstraints = {
      echoCancellation: true,
      noiseSuppression: true,
      autoGainControl: true,
    };

    if (this.platformInfo.isIOS) {
      // iOS works better with minimal constraints
      return {
        echoCancellation: true,
        noiseSuppression: true,
      };
    }

    if (this.platformInfo.isAndroid) {
      return {
        ...base,
        // Android Chrome benefits from specific sample rate
        sampleRate: { ideal: 44100 },
        channelCount: { ideal: 1 },
      };
    }

    // Desktop: full constraints
    return {
      ...base,
      sampleRate: { ideal: 48000 },
      channelCount: { ideal: 1 },
    };
  }

  /**
   * Resume AudioContext if suspended (required for iOS)
   */
  private async ensureAudioContextResumed(ctx: AudioContext): Promise<void> {
    if (ctx.state === 'suspended') {
      console.log('AudioContext suspended, attempting resume...');
      try {
        await ctx.resume();
        console.log('AudioContext resumed successfully');
      } catch (err) {
        console.error('Failed to resume AudioContext:', err);
        throw new Error('Could not activate audio. Please tap the screen and try again.');
      }
    }
    
    // Double-check state
    if (ctx.state !== 'running') {
      console.warn('AudioContext state after resume:', ctx.state);
    }
  }

  /**
   * Setup audio level monitoring
   */
  private setupLevelMonitoring(ctx: AudioContext, source: MediaStreamAudioSourceNode) {
    this.analyser = ctx.createAnalyser();
    this.analyser.fftSize = 256;
    this.analyser.smoothingTimeConstant = 0.3;
    source.connect(this.analyser);
    
    const dataArray = new Uint8Array(this.analyser.frequencyBinCount);
    this.hasDetectedAudio = false;
    this.peakLevel = 0;
    
    this.levelCheckInterval = setInterval(() => {
      if (!this.analyser) return;
      
      this.analyser.getByteFrequencyData(dataArray);
      
      // Calculate RMS level
      let sum = 0;
      for (let i = 0; i < dataArray.length; i++) {
        sum += dataArray[i] * dataArray[i];
      }
      const rms = Math.sqrt(sum / dataArray.length);
      const normalizedLevel = Math.min(1, rms / 128);
      
      // Track peak level
      if (normalizedLevel > this.peakLevel) {
        this.peakLevel = normalizedLevel;
      }
      
      // Detect if we've captured any meaningful audio (threshold ~0.05)
      if (normalizedLevel > 0.05) {
        this.hasDetectedAudio = true;
      }
      
      // Callback for UI visualization
      if (this.onAudioLevelCallback) {
        this.onAudioLevelCallback(normalizedLevel);
      }
    }, 50);
  }

  /**
   * Check if we're in a secure context (HTTPS or localhost)
   */
  private checkSecureContext(): void {
    // Check if mediaDevices API is available
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      // Determine the likely cause
      if (window.location.protocol === 'http:' && window.location.hostname !== 'localhost') {
        throw new Error('Microphone requires HTTPS. Please access the site using https://');
      }
      throw new Error('Microphone not supported in this browser. Try Chrome or Safari.');
    }
  }

  /**
   * Start recording with iOS WAV fallback
   */
  private async startIOSRecording(options: StartOptions): Promise<void> {
    this.usingWav = true;
    this.mimeType = 'audio/wav';
    this.wavBuffers = [];

    // Check secure context first
    this.checkSecureContext();

    const AudioContextClass = (window as any).AudioContext || (window as any).webkitAudioContext;
    if (!AudioContextClass) {
      throw new Error('Web Audio API not supported on this device');
    }

    // Get microphone stream
    console.log('Requesting microphone access (iOS)...');
    this.stream = await navigator.mediaDevices.getUserMedia({ 
      audio: this.getAudioConstraints() 
    });
    console.log('Microphone access granted');

    // Create and resume AudioContext
    const wavCtx = new AudioContextClass() as AudioContext;
    this.wavCtx = wavCtx;
    await this.ensureAudioContextResumed(wavCtx);
    
    this.wavSampleRate = wavCtx.sampleRate;
    console.log('AudioContext sample rate:', this.wavSampleRate);

    // Setup audio nodes
    const wavSource = wavCtx.createMediaStreamSource(this.stream);
    const wavProcessor = wavCtx.createScriptProcessor(4096, 1, 1);
    const wavGain = wavCtx.createGain();
    wavGain.gain.value = 0; // Prevent feedback
    
    this.wavSource = wavSource;
    this.wavProcessor = wavProcessor;
    this.wavGain = wavGain;

    // Setup level monitoring
    this.setupLevelMonitoring(wavCtx, wavSource);
    this.onAudioLevelCallback = options.onAudioLevel;

    // Capture audio data
    wavProcessor.onaudioprocess = (e: AudioProcessingEvent) => {
      if (this.state !== 'recording') return;
      
      const input = e.inputBuffer.getChannelData(0);
      // Copy buffer (input is reused by browser)
      this.wavBuffers.push(new Float32Array(input));
    };

    // Connect nodes
    wavSource.connect(wavProcessor);
    wavProcessor.connect(wavGain);
    wavGain.connect(wavCtx.destination);

    // Small warmup delay to ensure mic is ready
    await new Promise(resolve => setTimeout(resolve, 100));
    
    this.state = 'recording';
    console.log('iOS WAV recorder started successfully');
  }

  /**
   * Start recording with MediaRecorder (Android/Desktop)
   */
  private async startMediaRecorderRecording(options: StartOptions): Promise<void> {
    this.usingWav = false;
    
    // Check secure context first
    this.checkSecureContext();
    
    // Determine best mime type for platform
    const preferredTypes = this.platformInfo.isAndroid ? [
      'audio/webm;codecs=opus',
      'audio/webm',
      'audio/ogg;codecs=opus',
      'audio/ogg',
    ] : [
      'audio/webm;codecs=opus',
      'audio/webm',
      'audio/ogg',
      'audio/mp4',
    ];

    // Find supported type
    const supportedType = preferredTypes.find(type => 
      MediaRecorder && MediaRecorder.isTypeSupported && MediaRecorder.isTypeSupported(type)
    );

    if (supportedType) {
      this.mimeType = supportedType;
    } else {
      console.warn('No preferred codec supported, using browser default');
      this.mimeType = 'audio/webm';
    }

    console.log('Using mimeType:', this.mimeType);

    // Get microphone stream
    console.log('Requesting microphone access...');
    this.stream = await navigator.mediaDevices.getUserMedia({ 
      audio: this.getAudioConstraints() 
    });
    console.log('Microphone access granted');

    this.chunks = [];

    // Create MediaRecorder
    try {
      if (supportedType) {
        this.mediaRecorder = new MediaRecorder(this.stream, { mimeType: this.mimeType });
      } else {
        this.mediaRecorder = new MediaRecorder(this.stream);
        this.mimeType = (this.mediaRecorder as any).mimeType || 'audio/webm';
      }
    } catch (err) {
      console.error('MediaRecorder creation failed:', err);
      throw new Error('Could not start recording. Please try again.');
    }

    // Setup audio level monitoring via AudioContext
    const AudioContextClass = (window as any).AudioContext || (window as any).webkitAudioContext;
    if (AudioContextClass) {
      try {
        const ctx = new AudioContextClass();
        await this.ensureAudioContextResumed(ctx);
        const source = ctx.createMediaStreamSource(this.stream);
        this.setupLevelMonitoring(ctx, source);
        this.onAudioLevelCallback = options.onAudioLevel;
        this.wavCtx = ctx; // Store for cleanup
      } catch (err) {
        console.warn('Could not setup level monitoring:', err);
      }
    }

    // Handle data chunks
    this.mediaRecorder.ondataavailable = (e: BlobEvent) => {
      if (e.data && e.data.size > 0) {
        this.chunks.push(e.data);
        if (this.onChunkCallback) {
          try {
            this.onChunkCallback(new Blob(this.chunks, { type: this.mimeType }));
          } catch (error) {
            console.error('Error in onChunkCallback:', error);
          }
        }
      }
    };

    this.mediaRecorder.onerror = (event: any) => {
      console.error('MediaRecorder error:', event.error);
    };

    // Start recording
    this.mediaRecorder.start(options.timesliceMs || 0);
    
    // Warmup delay
    await new Promise(resolve => setTimeout(resolve, 100));
    
    this.state = 'recording';
    console.log('MediaRecorder started successfully');
  }

  /**
   * Main start method - automatically chooses best approach for platform
   */
  async start(options: StartOptions = {}): Promise<void> {
    if (this.state !== 'idle') {
      console.warn('Recorder already active, stopping first...');
      await this.stop();
    }

    this.state = 'starting';
    this.hasDetectedAudio = false;
    this.peakLevel = 0;

    console.log('Starting recorder on platform:', this.platformInfo.description);
    console.log('MediaRecorder available:', typeof MediaRecorder !== 'undefined');

    try {
      if (this.platformInfo.isIOSWebKit) {
        await this.startIOSRecording(options);
      } else {
        await this.startMediaRecorderRecording(options);
      }
    } catch (error: any) {
      this.state = 'idle';
      
      // Provide user-friendly error messages
      if (error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError') {
        throw new Error('Microphone permission denied. Please allow microphone access in your browser settings.');
      }
      if (error.name === 'NotFoundError' || error.name === 'DevicesNotFoundError') {
        throw new Error('No microphone found. Please connect a microphone and try again.');
      }
      if (error.name === 'NotReadableError' || error.name === 'TrackStartError') {
        throw new Error('Microphone is in use by another app. Please close other apps using the mic.');
      }
      
      throw error;
    }
  }

  /**
   * Stop recording and return audio blob
   */
  async stop(): Promise<Blob> {
    if (this.state === 'idle') {
      console.warn('Recorder not active');
      return new Blob([], { type: 'audio/webm' });
    }

    this.state = 'stopping';
    
    // Stop level monitoring
    if (this.levelCheckInterval) {
      clearInterval(this.levelCheckInterval);
      this.levelCheckInterval = undefined;
    }
    
    if (this.analyser) {
      try { this.analyser.disconnect(); } catch (_) {}
      this.analyser = undefined;
    }

    let blob: Blob;

    if (this.usingWav) {
      // iOS WAV path
      if (this.stream) {
        this.stream.getTracks().forEach(t => t.stop());
        this.stream = undefined;
      }

      try {
        this.wavSource?.disconnect();
        this.wavProcessor?.disconnect();
        this.wavGain?.disconnect();
      } catch (_) {}

      try {
        await this.wavCtx?.close();
      } catch (_) {}

      blob = this.encodeWav(this.wavBuffers, this.wavSampleRate);
      
      // Cleanup
      this.wavBuffers = [];
      this.wavCtx = undefined;
      this.wavSource = undefined;
      this.wavProcessor = undefined;
      this.wavGain = undefined;
      this.usingWav = false;
    } else {
      // MediaRecorder path
      if (!this.mediaRecorder) {
        console.warn('MediaRecorder not initialized');
        blob = new Blob([], { type: 'audio/webm' });
      } else {
        await new Promise<void>((resolve) => {
          const mr = this.mediaRecorder!;
          const prevOnStop = mr.onstop;
          
          mr.onstop = () => {
            setTimeout(() => {
              mr.onstop = prevOnStop as any;
              resolve();
            }, 50); // Small delay for final data
          };

          try {
            (mr as any).requestData?.();
          } catch (_) {}

          if (mr.state !== 'inactive') {
            mr.stop();
          } else {
            resolve();
          }
        });

        blob = new Blob(this.chunks, { type: this.mimeType });
        this.chunks = [];
        this.mediaRecorder = undefined;
      }

      if (this.stream) {
        this.stream.getTracks().forEach(t => t.stop());
        this.stream = undefined;
      }

      // Cleanup level monitoring AudioContext
      try {
        await this.wavCtx?.close();
      } catch (_) {}
      this.wavCtx = undefined;
    }

    this.onChunkCallback = undefined;
    this.onAudioLevelCallback = undefined;
    this.state = 'idle';

    // Log recording stats
    console.log('Recording stopped:', {
      size: blob.size,
      type: blob.type,
      hasDetectedAudio: this.hasDetectedAudio,
      peakLevel: this.peakLevel.toFixed(3),
      platform: this.platformInfo.description
    });

    return blob;
  }

  /**
   * Check if actual audio was detected during recording
   */
  wasAudioDetected(): boolean {
    return this.hasDetectedAudio;
  }

  /**
   * Get peak audio level from last recording (0-1)
   */
  getPeakLevel(): number {
    return this.peakLevel;
  }

  /**
   * Get current recorder state
   */
  getState(): RecorderState {
    return this.state;
  }

  /**
   * Get platform info
   */
  getPlatformInfo() {
    return this.platformInfo;
  }

  /**
   * Register chunk callback for streaming
   */
  onChunk(callback?: (blob: Blob) => void) {
    this.onChunkCallback = callback;
  }

  /**
   * Cancel recording without returning data
   */
  async cancel(): Promise<void> {
    this.chunks = [];
    this.wavBuffers = [];
    await this.stop();
  }
}

const recorder = new AudioRecorder();

export default recorder;
